<template>
  <HLSpace vertical :size="24">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-3xl font-bold text-gray-900">Test Cases</h1>
        <p class="text-gray-600">Manage and organize test cases</p>
      </div>
      <div class="flex items-center space-x-4">
        <Button
          @click="refreshData"
          variant="outline"
          :loading="loading"
        >
          <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </Button>
        <Button
          @click="createTestCase"
          :disabled="!canCreate"
        >
          <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Create Test Case
        </Button>
      </div>
    </div>

    <!-- Filters -->
    <div class="bg-white rounded-lg shadow p-6">
      <div class="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Project</label>
          <Select
            v-model="filters.projectId"
            :options="projectOptions"
            placeholder="All Projects"
            @change="applyFilters"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Priority</label>
          <Select
            v-model="filters.priority"
            :options="priorityOptions"
            placeholder="All Priorities"
            @change="applyFilters"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Status</label>
          <Select
            v-model="filters.status"
            :options="statusOptions"
            placeholder="All Statuses"
            @change="applyFilters"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Assignee</label>
          <Select
            v-model="filters.assigneeId"
            :options="assigneeOptions"
            placeholder="All Assignees"
            @change="applyFilters"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Search</label>
          <Input
            v-model="filters.search"
            placeholder="Search test cases..."
            @input="applyFilters"
          />
        </div>
      </div>
    </div>

    <!-- Test Cases List -->
    <div class="bg-white rounded-lg shadow">
      <div v-if="loading" class="p-8 flex flex-col items-center justify-center">
        <HLSpin id="testcases-list-loading-spinner" size="lg" :show="loading">
          <template #description>
            <HLText variant="body-sm" color="muted">Loading test cases...</HLText>
          </template>
        </HLSpin>
      </div>
      <div v-else-if="testCases.length === 0" class="p-8 text-center">
        <EmptyState
          title="No test cases found"
          description="Create your first test case to get started."
        />
      </div>
      <div v-else class="overflow-hidden">
        <Table
          :headers="tableHeaders"
          :data="testCases"
          :loading="loading"
          @row-click="viewTestCase"
        >
          <template #cell-testCaseId="{ row }">
            <div class="flex items-center">
              <span class="text-sm font-mono text-gray-600 bg-gray-100 px-2 py-1 rounded">
                {{ row.testCaseId || 'N/A' }}
              </span>
            </div>
          </template>
          
          <template #cell-name="{ row }">
            <div class="flex items-center space-x-3">
              <div class="flex-shrink-0">
                <div class="w-8 h-8 bg-blue-100 rounded-md flex items-center justify-center">
                  <svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
              </div>
              <div>
                <p class="text-sm font-medium text-gray-900">{{ row.name }}</p>
                <p class="text-sm text-gray-500">{{ row.projectName }}</p>
              </div>
            </div>
          </template>
          
          <template #cell-priority="{ row }">
            <HLBadge :id="`testcase-priority-${row.id}`" :color="getPriorityVariant(row.priority)" size="sm">
              {{ row.priority }}
            </HLBadge>
          </template>
          
          <template #cell-status="{ row }">
            <HLBadge :id="`testcase-status-${row.id}`" :color="getStatusVariant(row.status)" size="sm">
              {{ row.status }}
            </HLBadge>
          </template>
          
          <template #cell-assignee="{ row }">
            <div class="flex items-center space-x-2">
              <div class="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center">
                <svg class="w-3 h-3 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <span class="text-sm text-gray-900">{{ row.assigneeName || 'Unassigned' }}</span>
            </div>
          </template>
          
          <template #cell-tags="{ row }">
            <div class="flex flex-wrap gap-1">
              <Tag
                v-for="tag in (row.tags || []).slice(0, 2)"
                :key="tag"
                size="sm"
                variant="secondary"
              >
                {{ tag }}
              </Tag>
              <span v-if="(row.tags || []).length > 2" class="text-xs text-gray-500">
                +{{ (row.tags || []).length - 2 }} more
              </span>
            </div>
          </template>
          
          <template #cell-actions="{ row }">
            <div class="flex items-center space-x-2">
              <Button
                @click.stop="viewTestCase(row)"
                variant="outline"
                size="sm"
              >
                View
              </Button>
              <Button
                v-if="canEdit(row)"
                @click.stop="editTestCase(row)"
                variant="outline"
                size="sm"
              >
                Edit
              </Button>
              <Button
                v-if="canDelete(row)"
                @click.stop="deleteTestCase(row)"
                variant="outline"
                size="sm"
                class="text-red-600 hover:text-red-700"
              >
                Delete
              </Button>
            </div>
          </template>
        </Table>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="totalPages > 1" class="flex items-center justify-between">
      <div class="text-sm text-gray-700">
        Showing {{ (currentPage - 1) * pageSize + 1 }} to {{ Math.min(currentPage * pageSize, totalItems) }} of {{ totalItems }} results
      </div>
      <div class="flex items-center space-x-2">
        <Button
          @click="goToPage(currentPage - 1)"
          :disabled="currentPage === 1"
          variant="outline"
          size="sm"
        >
          Previous
        </Button>
        <span class="text-sm text-gray-700">
          Page {{ currentPage }} of {{ totalPages }}
        </span>
        <Button
          @click="goToPage(currentPage + 1)"
          :disabled="currentPage === totalPages"
          variant="outline"
          size="sm"
        >
          Next
        </Button>
      </div>
    </div>
  </HLSpace>
</template>

<script setup>
import { ref, computed, onMounted, onActivated, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useTestCaseStore } from '@/stores/testCase'
import { useProjectStore } from '@/stores/project'
import { useUserStore } from '@/stores/user'
import { useAuthStore } from '@/stores/auth'
import { useToast } from '@/composables/useToast'
import { PRIORITY_LEVELS, TEST_CASE_STATUS } from '@/constants/testCaseConstants'
import { HLButton, HLBadge, HLTag, HLSpace, HLSpin, HLText } from '@platform-ui/highrise'
import Table from '@/components/ui/Table.vue'
import Input from '@/components/ui/Input.vue'
import Select from '@/components/ui/Select.vue'
import EmptyState from '@/components/ui/EmptyState.vue'

const router = useRouter()
const route = useRoute()
const testCaseStore = useTestCaseStore()
const projectStore = useProjectStore()
const userStore = useUserStore()
const authStore = useAuthStore()
const { showToast } = useToast()

const loading = ref(false)
const testCases = ref([])
const projects = ref([])
const users = ref([])

const filters = ref({
  projectId: '',
  priority: '',
  status: '',
  assigneeId: '',
  search: ''
})

const pagination = ref({
  page: 1,
  pageSize: 10,
  total: 0
})


const tableHeaders = [
  { key: 'testCaseId', label: 'Test Case ID' },
  { key: 'name', label: 'Name' },
  { key: 'priority', label: 'Priority' },
  { key: 'status', label: 'Status' },
  { key: 'assignee', label: 'Assignee' },
  { key: 'tags', label: 'Tags' },
  { key: 'actions', label: 'Actions' }
]

const priorityOptions = [
  { value: '', label: 'All Priorities' },
  ...PRIORITY_LEVELS
]

const statusOptions = [
  { value: '', label: 'All Statuses' },
  ...TEST_CASE_STATUS
]

const projectOptions = computed(() => [
  { value: '', label: 'All Projects' },
  ...projects.value.map(project => ({
    value: project.id,
    label: project.name
  }))
])

const assigneeOptions = computed(() => [
  { value: '', label: 'All Assignees' },
  ...users.value.map(user => ({
    value: user.id,
    label: user.name
  }))
])

const currentPage = computed(() => pagination.value.page)
const pageSize = computed(() => pagination.value.pageSize)
const totalItems = computed(() => pagination.value.total)
const totalPages = computed(() => Math.ceil(totalItems.value / pageSize.value))

const canCreate = computed(() => {
  return authStore.user?.role === 'ADMIN' || 
         authStore.user?.role === 'QA_MANAGER' ||
         authStore.user?.role === 'QA_ENGINEER'
})

const getPriorityVariant = (priority) => {
  const variants = {
    'low': 'secondary',
    'medium': 'info',
    'high': 'warning',
    'critical': 'danger'
  }
  return variants[priority] || 'secondary'
}

const getStatusVariant = (status) => {
  const variants = {
    'draft': 'secondary',
    'active': 'info',
    'deprecated': 'danger'
  }
  return variants[status] || 'secondary'
}

const canEdit = (testCase) => {
  if (!authStore.user) return false
  return authStore.user.role === 'ADMIN' || 
         authStore.user.role === 'QA_MANAGER' ||
         testCase.assigneeId === authStore.user.id
}

const canDelete = (testCase) => {
  if (!authStore.user) return false
  return authStore.user.role === 'ADMIN' || 
         authStore.user.role === 'QA_MANAGER'
}

// OPTIMIZATION: SessionStorage caching
const CACHE_KEY_PREFIX = 'testcases_list_'
const CACHE_DURATION = 30000 // 30 seconds

const getCache = (key) => {
  try {
    const cached = sessionStorage.getItem(CACHE_KEY_PREFIX + key)
    if (cached) {
      const { data, timestamp } = JSON.parse(cached)
      const now = Date.now()
      if ((now - timestamp) < CACHE_DURATION) {
        return data
      }
    }
  } catch (e) {
    // Ignore cache errors
  }
  return null
}

const setCache = (key, data) => {
  try {
    sessionStorage.setItem(CACHE_KEY_PREFIX + key, JSON.stringify({
      data,
      timestamp: Date.now()
    }))
  } catch (e) {
    // Ignore cache errors (quota exceeded, etc.)
  }
}

const loadTestCases = async () => {
  loading.value = true
  try {
    const response = await testCaseStore.getTestCases({
      ...filters.value,
      page: pagination.value.page,
      pageSize: pagination.value.pageSize
    })
    
    let loadedTestCases = response.data || []
    
    // Sort by creation timestamp in descending order (newest first)
    loadedTestCases.sort((a, b) => {
      const dateA = a.created_at || a.createdAt
      const dateB = b.created_at || b.createdAt
      let timeA = 0
      let timeB = 0
      
      if (dateA) {
        if (dateA instanceof Date) {
          timeA = dateA.getTime()
        } else if (typeof dateA === 'string') {
          const parsed = new Date(dateA).getTime()
          timeA = isNaN(parsed) ? 0 : parsed
        } else if (typeof dateA === 'number') {
          timeA = dateA
        }
      }
      
      if (dateB) {
        if (dateB instanceof Date) {
          timeB = dateB.getTime()
        } else if (typeof dateB === 'string') {
          const parsed = new Date(dateB).getTime()
          timeB = isNaN(parsed) ? 0 : parsed
        } else if (typeof dateB === 'number') {
          timeB = dateB
        }
      }
      
      return timeB - timeA // Descending order (newest first)
    })
    
    testCases.value = loadedTestCases
    pagination.value.total = response.total || 0
    
    // Cache the results
    setCache('testCases', { data: testCases.value, total: pagination.value.total, filters: filters.value, page: pagination.value.page })
  } catch (error) {
    console.error('Error loading test cases:', error)
    showToast('Error loading test cases', 'error')
    testCases.value = []
  } finally {
    loading.value = false
  }
}

const loadProjects = async () => {
  try {
    // Check cache first
    const cached = getCache('projects')
    if (cached) {
      projects.value = cached
      return
    }
    
    const response = await projectStore.getProjects()
    projects.value = response || []
    
    // Cache the results
    setCache('projects', projects.value)
  } catch (error) {
    console.error('Error loading projects:', error)
  }
}

const loadUsers = async () => {
  try {
    // Check cache first
    const cached = getCache('users')
    if (cached) {
      users.value = cached
      return
    }
    
    const response = await userStore.getUsers()
    users.value = response || []
    
    // Cache the results
    setCache('users', users.value)
  } catch (error) {
    console.error('Error loading users:', error)
  }
}

const applyFilters = () => {
  pagination.value.page = 1
  loadTestCases()
}

const refreshData = () => {
  loadTestCases()
}

const goToPage = (page) => {
  if (page >= 1 && page <= totalPages.value) {
    pagination.value.page = page
    loadTestCases()
  }
}

const viewTestCase = (testCase) => {
  const projectId = route.params.projectId
  if (projectId) {
    router.push(`/projects/${projectId}/test-cases/${testCase.id}`)
  } else {
    showToast('Project ID not found. Please navigate from a project context.', 'error')
  }
}

const editTestCase = (testCase) => {
  // TODO: Implement edit functionality
  showToast('Edit functionality coming soon', 'info')
}

const deleteTestCase = async (testCase) => {
  if (!confirm(`Are you sure you want to delete "${testCase.name}"?`)) {
    return
  }

  try {
    await testCaseStore.deleteTestCase(testCase.id)
    showToast('Test case deleted successfully', 'success')
    loadTestCases()
  } catch (error) {
    console.error('Error deleting test case:', error)
    showToast('Error deleting test case', 'error')
  }
}

const createTestCase = (e) => {
  const projectId = route.params.projectId
  if (projectId) {
    const path = `/projects/${projectId}/test-cases/create`
    if (e && (e.metaKey || e.ctrlKey)) {
      window.open(path, '_blank')
    } else {
      router.push(path)
    }
  } else {
    showToast('Project ID not found. Please navigate from a project context.', 'error')
  }
}

// Watch for filter changes
watch(filters, () => {
  applyFilters()
}, { deep: true })

onMounted(() => {
  // OPTIMIZATION: Try to load from cache first
  const cachedTestCases = getCache('testCases')
  const cachedProjects = getCache('projects')
  const cachedUsers = getCache('users')
  
  if (cachedTestCases && cachedProjects && cachedUsers) {
    // Check if filters match cached filters
    const filtersMatch = JSON.stringify(cachedTestCases.filters) === JSON.stringify(filters.value) &&
                         cachedTestCases.page === pagination.value.page
    
    if (filtersMatch) {
      testCases.value = cachedTestCases.data || []
      pagination.value.total = cachedTestCases.total || 0
      projects.value = cachedProjects
      users.value = cachedUsers
      loading.value = false
      return
    }
  }
  
  // Load fresh data (will cache after loading)
  loadProjects()
  loadUsers()
  loadTestCases()
})

// OPTIMIZATION: Check cache when component is activated (tab switching)
onActivated(() => {
  const cachedTestCases = getCache('testCases')
  const cachedProjects = getCache('projects')
  const cachedUsers = getCache('users')
  
  if (cachedTestCases && cachedProjects && cachedUsers) {
    const filtersMatch = JSON.stringify(cachedTestCases.filters) === JSON.stringify(filters.value) &&
                         cachedTestCases.page === pagination.value.page
    
    if (filtersMatch && testCases.value.length === 0) {
      testCases.value = cachedTestCases.data || []
      pagination.value.total = cachedTestCases.total || 0
      projects.value = cachedProjects
      users.value = cachedUsers
      loading.value = false
      return
    }
  }
  
  // Only reload if data is missing
  if (testCases.value.length === 0) loadTestCases()
  if (projects.value.length === 0) loadProjects()
  if (users.value.length === 0) loadUsers()
})
</script>